/* script */
//
//
//

// Utils
import { cloneDeep, get } from 'lodash';
import { satisfies } from 'semver';

// Mixins
import unlayerMixin from 'shared/ui/mixins/unlayer';

// Constants
import DEFAULT_CONFIG from './unlayer_editor_config.json';

import qrCode from './unlayer-qr-code';

/**
 * @typedef {Object} UnlayerFile
 * @property {File[]} accepted
 * @property {File[]} attachments
 */
/**
 * @typedef {Object} UnlayerAssetProviderParams
 * @property {number} [page] Current page
 * @property {number} [perPage] How many images to show on each page
 */
const __vue_script__ = {
    name: 'UnlayerEditor',

    mixins: [unlayerMixin],

    props: {
        value: {
            type: Object,
            default: () => undefined
        },

        options: {
            type: Object,
            default: () => ({})
        },

        eid: {
            type: String,
            default: null
        }
    },

    data() {
        return {
            containerId: `editor-id-${Date.now()}`,
            design: undefined,
            editor: undefined,
            userTags: {}
        };
    },

    watch: {
        value(design) {
            // Only update the content if it's changed from an external source
            // or else it'll act weird when you try to type anything
            if (design !== this.design) {
                this.loadDesign(design);
                // do not emit on the first change
                if (this.design) {
                    this.$emit('input', design);
                }
                this.design = design;
            }
        },
    },

    async mounted() {
        try {
            await this.loadUnlayerScript();
            await this.transformUserMetadataToTags();
            await this.initEditor();
            this.loadDesign(this.value);

            window.addEventListener('theme-changed', this.themeChangedHandler);
        } catch (error) {
            console.error('[UnlayerEditor] Could not start the editor', error);

            this.$notify({
                group: 'flashes',
                title: this.$i18n.t('general.error_occurred').toString(),
                text: this.$i18n.t('email_templates_editor.messages.not_loaded').toString(),
                type: 'error',
            });
        }
    },

    beforeDestroy() {
        window.removeEventListener('theme-changed', this.themeChangedHandler);
        this.destroy();
    },

    methods: {
        /**
         * strips the "-beta" part from the version
         * "semver" library does not handle it very well
         * ex: `semver.satisfies("5.2.1-beta.2", ">=4.0.0")` returns false
         * @param {string} [version]
         */
        normalizeVersion(version) {
            if (!version?.includes('-beta')) return version;
            return version.split('-beta')[0];
        },

        async transformUserMetadataToTags() {
            const personMetadata = await this.$services.metadata.getMetadataForTypeAsArray(this.eid, 'person');
            this.userTags.mergeTags = {
                event_name: {
                    name: 'Event Name',
                    value: '{{_event_name}}'
                },
                participant_details: {
                    name: 'Participant details',
                    mergeTags: {}
                }
            };

            const userMergeTags = get(this.userTags, 'mergeTags.participant_details.mergeTags');
            personMetadata.map(descriptor => {
                if (descriptor.kind && descriptor.kind === 'boolean') return;

                userMergeTags[descriptor.field] = {
                    name: descriptor.label || descriptor.field,
                    value: `{{${descriptor.field}}}`
                };
            });

            const packages = await this.$services.market?.getInstalledPackages(this.eid);
            if (packages?.agenda) {
                Object.assign(this.userTags.mergeTags, this.getAgendaMergeTags());
            }
            if (packages?.meetings || packages?.appointments) {
                const meetingsPkgVersion = this.normalizeVersion(packages?.appointments || packages?.meetings);
                Object.assign(
                    this.userTags.mergeTags,
                    this.getMeetingsMergeTags(meetingsPkgVersion),
                );
            }
        },

        async initEditor() {
            if (this.editor) {
                console.warn('[UnlayerEditor] Editor already initialised');
                return;
            }

            const theme = this.$utils.uiTheme.getCurrentTheme(this.$cookies);
            const colors = this.$utils.uiTheme.getColorsAsCSSProps(theme);
            const config = cloneDeep(DEFAULT_CONFIG);
            config.customCSS.push(`:root { ${colors} }`);

            this.editor = await new Promise((resolve) => {
                const editor = this.unlayer.createEditor({
                    id: this.containerId,
                    displayMode: 'email',
                    ...config,
                    ...this.options,
                    ...this.userTags,
                    customJS: [
                        ...(config?.customJS || []),
                        ...(this.options?.customJS || []),
                        qrCode,
                    ],
                    appearance: {
                        theme: `modern_${theme}`
                    }
                });

                editor.addEventListener('design:updated', this.onChangeHandler);
                editor.registerCallback('image', this.onImageUpload);
                editor.registerProvider('userUploads', this.provideImages);

                editor.addEventListener('editor:ready', function onReady() {
                    editor.removeEventListener('editor:ready', onReady);
                    resolve(editor);
                });
            });

            this.$emit('loaded', true);
        },

        loadDesign(design) {
            if (!design || !this.editor) { return; }
            this.editor.loadDesign(design);
        },

        /**
         * Editor changes handler
         */
        onChangeHandler() {
            this.editor?.exportHtml(({ design }) => {
                this.design = design;
                this.$emit('input', this.design);
            });
        },

        /**
         * Stored images provider
         *
         * @param {UnlayerAssetProviderParams} params
         * @param {Function} done
         */
        async provideImages(params, done) {
            try {
                const { page, perPage } = params;
                const { assets, total, hasMore } = await this.$services.publicAssets.getAllAssets(this.eid, page, perPage);

                done(assets, { hasMore, page, perPage, total });
            } catch (error) {
                console.error('[UnlayerEditor] Could not load event assets', error.message);
                done([]);
            }
        },

        /**
         * Used during the save process to get the current design/html
         */
        async getDesignAndHtml() {
            return new Promise(resolve => {
                this.editor.exportHtml(resolve);
            });
        },

        /**
         * Returns merge tags for agenda
         * @returns {object}
         */
        getAgendaMergeTags() {
            return {
                session_details: {
                    name: this.$i18n.t('email_templates_editor.merge_tags.session.section_name').toString(),
                    mergeTags: {
                        waitlisted_session: {
                            name: this.$i18n.t('email_templates_editor.merge_tags.session.waitlisted_session').toString(),
                            value: '{{waitlisted_session}}'
                        },
                        lease_time: {
                            name: this.$i18n.t('email_templates_editor.merge_tags.session.lease_time').toString(),
                            value: '{{lease_time}}'
                        }
                    }
                }
            };
        },

        /**
         * Returns merge tags for meetings
         * @param {string} meetingsPkgVersion
         * @returns {object}
         */
        getMeetingsMergeTags(meetingsPkgVersion) {
            const mergeTags = {
                meeting_title: {
                    name: this.$i18n.t('email_templates_editor.merge_tags.meeting.title').toString(),
                    value: '{{meeting_title}}'
                },
                meeting_date: {
                    name: this.$i18n.t('email_templates_editor.merge_tags.meeting.date').toString(),
                    value: '{{meeting_date}}'
                },
                meeting_time: {
                    name: this.$i18n.t('email_templates_editor.merge_tags.meeting.time').toString(),
                    value: '{{meeting_time}}'
                },
                meeting_location: {
                    name: this.$i18n.t('email_templates_editor.merge_tags.meeting.location').toString(),
                    value: '{{meeting_location}}'
                },
                meeting_participant_count: {
                    name: this.$t('email_templates_editor.merge_tags.meeting.participant_count').toString(),
                    value: '{{meeting_participant_count}}'
                },
                meeting_status: {
                    name: this.$t('email_templates_editor.merge_tags.meeting.status').toString(),
                    value: '{{meeting_status}}'
                },
            };
            // this works for both old and new meetings
            mergeTags.meeting_organizer = {
                name: this.$i18n.t('email_templates_editor.merge_tags.meeting.organizer').toString(),
                value: '{{meeting_organizer}}'
            };
            // remove this once we have all events running meetings pkg ^4.0.0
            if (satisfies(meetingsPkgVersion, '>=4.0.0')) {
                mergeTags.meeting_organizer_fname = {
                    name: this.$i18n.t('email_templates_editor.merge_tags.meeting.organizer_fname').toString(),
                    value: '{{meeting_organizer_fname}}'
                };
                mergeTags.meeting_organizer_lname = {
                    name: this.$i18n.t('email_templates_editor.merge_tags.meeting.organizer_lname').toString(),
                    value: '{{meeting_organizer_lname}}'
                };
                mergeTags.meeting_organizer_email = {
                    name: this.$i18n.t('email_templates_editor.merge_tags.meeting.organizer_email').toString(),
                    value: '{{meeting_organizer_email}}'
                };
                mergeTags.meeting_description = {
                    name: this.$i18n.t('email_templates_editor.merge_tags.meeting.description').toString(),
                    value: '{{{meeting_description}}}'
                };
            }

            return {
                meeting_details: {
                    name: this.$i18n.t('email_templates_editor.merge_tags.meeting.section_name').toString(),
                    mergeTags
                }
            };
        },

        destroy() {
            this.editor?.removeEventListener('design:updated', this.onChangeHandler);
            this.editor?.unregisterCallback('image', this.onImageUpload);
            this.editor?.destroy();
            this.editor = null;
            this.$emit('loaded', false);
        },

        async themeChangedHandler({ detail }) {
            console.debug('[BaseCalendar] Theme changed, updating UI:', detail.theme);
            this.destroy();
            await this.initEditor();
            this.loadDesign(this.value);
        }
    }
};

/* template */
var __vue_render__ = function () {
  var _vm = this
  var _h = _vm.$createElement
  var _c = _vm._self._c || _h
  return _c("div", {
    staticClass: "unlayer-editor",
    attrs: { id: _vm.containerId },
  })
}
var __vue_staticRenderFns__ = []
__vue_render__._withStripped = true

  /* style */
  const __vue_inject_styles__ = undefined
  /* scoped */
  const __vue_scope_id__ = undefined
  /* module identifier */
  const __vue_module_identifier__ = undefined
  /* functional template */
  const __vue_is_functional_template__ = false
  /* component normalizer */
  function __vue_normalize__(
    template, style, script,
    scope, functional, moduleIdentifier, shadowMode,
    createInjector, createInjectorSSR, createInjectorShadow
  ) {
    const component = (typeof script === 'function' ? script.options : script) || {}

    // For security concerns, we use only base name in production mode.
    component.__file = "apps/shared/src/ui/components/editors/email/UnlayerEditor.vue"

    if (!component.render) {
      component.render = template.render
      component.staticRenderFns = template.staticRenderFns
      component._compiled = true

      if (functional) component.functional = true
    }

    component._scopeId = scope

    if (false) {
      let hook
      if (false) {
        // In SSR.
        hook = function(context) {
          // 2.3 injection
          context =
            context || // cached call
            (this.$vnode && this.$vnode.ssrContext) || // stateful
            (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext) // functional
          // 2.2 with runInNewContext: true
          if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
            context = __VUE_SSR_CONTEXT__
          }
          // inject component styles
          if (style) {
            style.call(this, createInjectorSSR(context))
          }
          // register component module identifier for async chunk inference
          if (context && context._registeredComponents) {
            context._registeredComponents.add(moduleIdentifier)
          }
        }
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        component._ssrRegister = hook
      }
      else if (style) {
        hook = shadowMode 
          ? function(context) {
              style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot))
            }
          : function(context) {
              style.call(this, createInjector(context))
            }
      }

      if (hook !== undefined) {
        if (component.functional) {
          // register for functional component in vue file
          const originalRender = component.render
          component.render = function renderWithStyleInjection(h, context) {
            hook.call(context)
            return originalRender(h, context)
          }
        } else {
          // inject component registration as beforeCreate hook
          const existing = component.beforeCreate
          component.beforeCreate = existing ? [].concat(existing, hook) : [hook]
        }
      }
    }

    return component
  }
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  const __vue_component__ = /*#__PURE__*/__vue_normalize__(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  )

  export default __vue_component__